import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ScheduleItem, TableColumn, ColumnFilters, ColumnFilterValue } from '@app-types';
import { AssessorMeetingsCellRenderer } from './AssessorMeetingsTableCells';
import { RZTable, ScheduleTableBody } from '@components/common/table';
import EmptyState from '@components/common/EmptyState';
import type { RenderCellParams } from '@components/common/table/ScheduleTableBody';
import { useTableExpand } from '@hooks/useTableExpand';

interface AssessorMeetingsTableProps {
  scheduleItems: ScheduleItem[];
  columns: TableColumn[];
  visibleColumns: Set<string>;
  onScheduleDate?: (item: ScheduleItem) => void;
  onAssignRole?: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
  onCreateAssessment?: (item: ScheduleItem, createForGroup?: boolean, contractId?: number) => void;
  onAddToGroup?: (item: ScheduleItem) => void;
  onRemoveFromGroup?: (meetingId: number, contractId: number) => void;
  onColumnSort: (columnKey: string) => void;
  sortBy: string;
  sortOrder: string;
  getSortIndicator: (columnKey: string) => string | null;
  isColumnSorted: (columnKey: string) => boolean;
  columnFilters?: {
    values: ColumnFilters;
    onChange: (columnKey: string, value: ColumnFilterValue | null) => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
  };
  isEmpty?: boolean;
}

const AssessorMeetingsTable: React.FC<AssessorMeetingsTableProps> = ({
  scheduleItems,
  columns,
  visibleColumns,
  onScheduleDate,
  onAssignRole,
  onCreateAssessment,
  onAddToGroup,
  onRemoveFromGroup,
  onColumnSort,
  sortBy,
  sortOrder,
  getSortIndicator,
  isColumnSorted,
  columnFilters,
  isEmpty = false
}) => {
  const navigate = useNavigate();

  // Use generic expand/collapse hook
  const { expandedGroups, toggleExpand, isExpanded } = useTableExpand();

  // Check if a column should be visible
  const isColumnVisible = useCallback(
    (columnKey: string): boolean => {
      return visibleColumns.has(columnKey);
    },
    [visibleColumns]
  );

  // Get visible columns for rendering
  const getVisibleColumns = useCallback((): TableColumn[] => {
    return columns.filter((col) => isColumnVisible(col.key));
  }, [columns, isColumnVisible]);

  // Memoized cell renderer for performance
  const renderCell = useCallback(
    (params: RenderCellParams) => {
      const { column, item, contract, isGroupRow = false, isChildRow = false, contractsInGroup = [] } = params;

      return (
        <AssessorMeetingsCellRenderer
          column={column}
          item={item}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          contractsInGroup={contractsInGroup}
          expandedGroups={expandedGroups}
          onToggleExpand={toggleExpand}
          onNavigate={navigate}
          onCreateAssessment={onCreateAssessment}
          onScheduleDate={onScheduleDate}
          onAssignRole={onAssignRole}
          onAddToGroup={onAddToGroup}
          onRemoveFromGroup={onRemoveFromGroup}
        />
      );
    },
    [
      expandedGroups,
      toggleExpand,
      navigate,
      onCreateAssessment,
      onScheduleDate,
      onAssignRole,
      onAddToGroup,
      onRemoveFromGroup
    ]
  );

  // Get visible columns
  const visibleColumnsList = getVisibleColumns();
  // Add extra column for sticky Clear button when filters are active
  const hasActiveClearButton = columnFilters?.hasActiveFilters;
  const totalColumns = hasActiveClearButton ? visibleColumnsList.length + 1 : visibleColumnsList.length;

  return (
    <RZTable.Container className="scheduler-table">
      <RZTable.Header
        columns={visibleColumnsList}
        sorting={{
          sortConfig: { field: sortBy, direction: sortOrder as 'ASC' | 'DESC' },
          onSort: onColumnSort,
          isSorted: isColumnSorted
        }}
        columnFilters={columnFilters}
      />
      {isEmpty ? (
        <tbody>
          <tr>
            <td colSpan={totalColumns} className="table-empty-cell">
              <EmptyState
                type="meetings"
                title="No meetings found"
                description="No meetings match the current filter criteria."
              />
            </td>
          </tr>
        </tbody>
      ) : (
        <ScheduleTableBody
          scheduleItems={scheduleItems}
          columns={visibleColumnsList}
          isExpanded={isExpanded}
          renderCell={renderCell}
        />
      )}
    </RZTable.Container>
  );
};

export default AssessorMeetingsTable;
